import { baseApi } from '@/store/baseApi';

import type {
  LoginRequest,
  LoginResponse,
  MeResponse,
  OrgSetupApiPayload,
  RegisterApiPayload,
  RegisterResponse,
  ResetPasswordApiPayload,
  SocialProvider,
  TokenRefreshResponse,
  User,
} from '../../types';

export const authApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    login: build.mutation<LoginResponse, LoginRequest>({
      query: (body) => ({
        url: '/api/auth/v1/login/',
        method: 'POST',
        body,
      }),
    }),

    logout: build.mutation<void, void>({
      query: () => ({
        url: '/api/auth/v1/logout/',
        method: 'POST',
      }),
    }),

    refreshToken: build.mutation<TokenRefreshResponse, void>({
      query: () => ({
        url: '/api/auth/v1/token/refresh/',
        method: 'POST',
      }),
    }),

    register: build.mutation<RegisterResponse, RegisterApiPayload>({
      query: (body) => ({
        url: '/api/auth/v1/register/',
        method: 'POST',
        body,
      }),
    }),

    forgotPassword: build.mutation<void, { email: string }>({
      query: (body) => ({
        url: '/api/auth/v1/forgot/generate-token/',
        method: 'POST',
        body,
      }),
    }),

    checkResetToken: build.mutation<{ valid: boolean }, { token: string }>({
      query: (body) => ({
        url: '/api/auth/v1/forgot/check-token/',
        method: 'POST',
        body,
      }),
    }),

    resetPassword: build.mutation<{ valid: boolean }, ResetPasswordApiPayload>({
      query: (body) => ({
        url: '/api/auth/v1/forgot/reset-password/',
        method: 'POST',
        body,
      }),
    }),

    verifyEmail: build.mutation<{ verified: boolean; email: string }, { token: string }>({
      query: (body) => ({
        url: '/api/auth/v1/validate-token/',
        method: 'POST',
        body,
      }),
    }),

    generateEmailToken: build.mutation<void, { email: string }>({
      query: (body) => ({
        url: '/api/auth/v1/generate-email-token/',
        method: 'POST',
        body,
      }),
    }),

    socialLogin: build.mutation<
      LoginResponse,
      { provider: SocialProvider; code: string; redirect_uri?: string; code_verifier?: string }
    >({
      query: (body) => ({
        url: '/api/auth/v1/loginsocial/',
        method: 'POST',
        body,
      }),
    }),

    getMe: build.query<MeResponse, void>({
      query: () => '/api/auth/v1/me/',
      providesTags: ['User'],
    }),

    createOrganization: build.mutation<void, OrgSetupApiPayload>({
      query: (body) => ({
        url: '/api/auth/v1/organization/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User', 'Organization'],
    }),

    getProfile: build.query<User, void>({
      query: () => '/api/auth/v1/profile/',
      providesTags: ['User'],
    }),
  }),
});

export const {
  useLoginMutation,
  useLogoutMutation,
  useRefreshTokenMutation,
  useRegisterMutation,
  useForgotPasswordMutation,
  useCheckResetTokenMutation,
  useResetPasswordMutation,
  useVerifyEmailMutation,
  useGenerateEmailTokenMutation,
  useSocialLoginMutation,
  useCreateOrganizationMutation,
  useGetProfileQuery,
  useGetMeQuery,
  useLazyGetMeQuery,
} = authApi;
